import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Form,
  Input,
  Button,
  Card,
  Collapse,
  Checkbox,
  Select,
  message,
  Tooltip,
  Spin,
} from "antd";
import { Link, useLocation } from "react-router-dom";
import {
  InfoCircleOutlined,
  ArrowLeftOutlined,
  CheckCircleOutlined,
  LockOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { AUTH_TOKEN, DOMAIN_ID, ORG_ID, REACT_APP_API_TENANTS_URL } from "../../../constants";

const { Panel } = Collapse;
const { Option } = Select;

const LOG_TYPES = [
  "Authentication Logs",
  "Database Agents Logs",
  "Platform Audit Logs",
  "RADIUS Logs",
  "Workflow Logs",
];

// Output types with their configurations
const OUTPUT_CONFIG = {
  Splunk: {
    icon: "https://www.splunk.com/content/dam/splunk2/en_us/images/icon-library/footer/logo-splunk-corp-rgb-k-web.svg ",
    color: "#0070CD",
    title: "Splunk Integration",
    subtitle: "Connect to your Splunk instance via HTTP Event Collector",
    description:
      "Splunk allows you to search, analyze, and visualize machine data in real-time, transforming it into actionable insights.",
    documentationLink: "https://docs.fluentbit.io/manual/pipeline/outputs/splunk",
  },
  Syslog: {
    icon: "https://cdn-icons-png.flaticon.com/512/8888/8888205.png ",
    color: "#00A76F",
    title: "Syslog Integration",
    subtitle: "Stream logs to your Syslog server via UDP or TCP",
    description:
      "Send logs to any standard Syslog server with configurable facilities, severity levels, and transport protocols.",
    documentationLink: "https://docs.fluentbit.io/manual/pipeline/outputs/syslog",
  },
  HTTP: {
    icon: "https://cdn-icons-png.flaticon.com/512/2165/2165004.png",
    color: "#7635DC",
    title: "HTTP Output",
    subtitle: "Send logs to any HTTP/HTTPS endpoint",
    description:
      "Forward logs to web services or custom endpoints with configurable formats, headers, and authentication methods.",
    documentationLink: "https://docs.fluentbit.io/manual/pipeline/outputs/http",
  },
  Elasticsearch: {
    icon: "https://assets.zabbix.com/img/brands/elasticsearch.svg",
    color: "#F6AD37",
    title: "Elasticsearch Output",
    subtitle: "Index logs directly into Elasticsearch",
    description:
      "Store and analyze logs with powerful search capabilities and visualize them with Kibana dashboards.",
    documentationLink: "https://docs.fluentbit.io/manual/pipeline/outputs/elasticsearch",
  },
};

const SPLUNK_PARAMETERS = [
  {
    id: 1,
    label: "Host",
    key: "host",
    required: true,
    tooltip:
      "The hostname or IP address of your Splunk instance where the HTTP Event Collector (HEC) is configured. Example: splunk.company.com",
  },
  {
    id: 2,
    label: "Port",
    key: "port",
    required: true,
    type: "number",
    tooltip:
      "The port number where your Splunk HTTP Event Collector is listening. Default is 8088 for HTTPS and 8089 for HTTP.",
  },
  {
    id: 3,
    label: "Authentication Token",
    key: "authToken",
    required: true,
    tooltip:
      "The HEC token generated in your Splunk instance. This token authenticates and authorizes the connection to send data to Splunk.",
  },
  {
    id: 4,
    label: "Password",
    key: "password",
    required: true,
    type: "password",
    tooltip:
      "The password associated with your Splunk account. This is used for authentication when sending data to Splunk.",
  },
  {
    id: 5,
    label: "HTTP Username",
    key: "httpUsername",
    required: false,
    tooltip:
      "Optional username for HTTP basic authentication if your Splunk instance requires it. This is different from your Splunk login credentials.",
  },
  {
    id: 6,
    label: "HTTP Password",
    key: "httpPassword",
    required: false,
    type: "password",
    tooltip:
      "Optional password for HTTP basic authentication. Only required if HTTP Username is provided.",
  },
  {
    id: 7,
    label: "Log Type",
    key: "logType",
    required: true,
    type: "select",
    options: LOG_TYPES,
    tooltip:
      "Select the type of logs to forward to Splunk. This helps in organizing and filtering logs in your Splunk dashboard.",
  },
];

const HTTP_PARAMETERS = [
  {
    id: 1,
    label: "Host",
    key: "host",
    required: true,
    tooltip:
      "The hostname or IP address of your HTTP endpoint where logs will be sent. Example: logs.api.company.com",
  },
  {
    id: 2,
    label: "Port",
    key: "port",
    required: true,
    type: "number",
    defaultValue: 443,
    tooltip:
      "The port number of your HTTP endpoint. Default is 443 for HTTPS and 80 for HTTP. Make sure this port is accessible and allowed by your firewall.",
  },
  {
    id: 3,
    label: "URI",
    key: "uri",
    required: true,
    defaultValue: "/",
    tooltip:
      "The specific path on your HTTP endpoint where logs should be sent. Example: /api/v1/logs or /ingest",
  },
  {
    id: 4,
    label: "Content Type",
    key: "contentType",
    required: false,
    defaultValue: "application/json",
    tooltip:
      "The Content-Type header value for your HTTP requests. Common values are application/json or application/x-ndjson for newline-delimited JSON.",
  },
  {
    id: 5,
    label: "Custom Headers",
    key: "customHeaders",
    required: false,
    type: "textarea",
    tooltip:
      'Additional HTTP headers in JSON format. Example: {"Authorization": "Bearer token", "X-Custom-Header": "value"}',
  },

  {
    id: 9,
    label: "Use TLS",
    key: "useTls",
    type: "select",
    options: ["true", "false"],
    defaultValue: "true",
    required: false,
    tooltip:
      "Enable TLS for secure HTTPS connections. Strongly recommended for production environments to encrypt data in transit.",
  },
  {
    id: 10,
    label: "TLS Verify",
    key: "tlsVerify",
    type: "select",
    options: ["true", "false"],
    defaultValue: "true",
    required: true,
    tooltip:
      "Verify TLS certificates when making HTTPS connections. Disable only in development with self-signed certificates.",
  },
  {
    id: 11,
    label: "JSON Date Key",
    key: "jsonDateKey",
    required: false,
    defaultValue: "date",
    tooltip:
      "The key name for the timestamp field in your JSON output. Common values: date, timestamp, @timestamp",
  },
  {
    id: 12,
    label: "JSON Date Format",
    key: "jsonDateFormat",
    required: false,
    defaultValue: "iso8601",
    tooltip:
      "Format for timestamp values. iso8601 is recommended for compatibility. Example: 2024-03-21T14:30:00Z",
  },
  {
    id: 13,
    label: "Log Type",
    key: "logType",
    required: true,
    type: "select",
    options: LOG_TYPES,
    tooltip:
      "Select which type of logs to forward to your HTTP endpoint. This helps in organizing and filtering logs at the destination.",
  },
];

const SYSLOG_PARAMETERS = [
  {
    id: 1,
    label: "Host",
    key: "host",
    required: true,
    tooltip:
      "The hostname or IP address of your Syslog server. Ensure the server is configured to accept external Syslog messages.",
  },
  {
    id: 2,
    label: "Port",
    key: "port",
    required: true,
    type: "number",
    defaultValue: 514,
    tooltip:
      "The port your Syslog server is listening on. Standard ports are 514 for UDP/TCP or 6514 for TLS. Verify your firewall allows this port.",
  },
  {
    id: 3,
    label: "Mode",
    key: "mode",
    required: true,
    type: "select",
    options: ["TCP", "UDP"],
    defaultValue: "TCP",
    tooltip:
      "Transport protocol for sending logs. TCP ensures reliable delivery but may be slower, UDP is faster but messages could be lost.",
  },
  {
    id: 4,
    label: "Syslog Format",
    key: "syslogFormat",
    required: false,
    defaultValue: "rfc3164",
    tooltip:
      "Syslog message format specification. RFC3164 is the traditional format, RFC5424 is newer with structured data support.",
  },
  {
    id: 5,
    label: "Message Key",
    key: "messageKey",
    required: true,
    defaultValue: "message",
    tooltip:
      "The field name in your log data that contains the main message content to be sent to Syslog.",
  },

  {
    id: 10,
    label: "TLS Verify",
    key: "tlsVerify",
    type: "select",
    options: ["true", "false"],
    defaultValue: "true",
    required: true,
    tooltip:
      "Verify TLS certificates when using secure Syslog (TCP with TLS). Disable only in development with self-signed certificates.",
  },
  {
    id: 11,
    label: "Log Type",
    key: "logType",
    required: true,
    type: "select",
    options: LOG_TYPES,
    tooltip:
      "Select which type of logs to forward to Syslog. This helps in organizing and filtering logs at the destination.",
  },
];

const ELASTICSEARCH_PARAMETERS = [
  {
    id: 1,
    label: "Host",
    key: "host",
    required: true,
    tooltip:
      "The hostname or IP address of your Elasticsearch server. For clusters, specify the load balancer or primary node address.",
  },
  {
    id: 2,
    label: "Port",
    key: "port",
    required: true,
    type: "number",
    defaultValue: 9200,
    tooltip:
      "The HTTP port of your Elasticsearch server. Default is 9200 for REST API. For secure clusters, 9243 is common.",
  },
  {
    id: 3,
    label: "Index Name",
    key: "indexName",
    required: true,
    tooltip:
      "The Elasticsearch index name where logs will be stored. Can include date patterns like logs-YYYY.MM.DD for rotation. Example: app-logs-*",
  },
  {
    id: 4,
    label: "HTTP Username",
    key: "httpUser",
    required: false,
    tooltip:
      "Username for Elasticsearch basic authentication. Required if your cluster uses security features like X-Pack.",
  },
  {
    id: 5,
    label: "HTTP Password",
    key: "httpPasswd",
    required: false,
    type: "password",
    tooltip: "Password for Elasticsearch basic authentication. Required if username is provided.",
  },
  {
    id: 6,
    label: "Use TLS",
    key: "tls",
    type: "select",
    options: ["true", "false"],
    defaultValue: "true",
    required: false,
    tooltip:
      "Enable TLS for secure HTTPS connections to Elasticsearch. Strongly recommended for production environments.",
  },
  {
    id: 7,
    label: "TLS Verify",
    key: "tlsVerify",
    type: "select",
    options: ["true", "false"],
    defaultValue: "true",
    required: true,
    tooltip:
      "Verify TLS certificates when connecting to Elasticsearch. Disable only in development with self-signed certificates.",
  },
  {
    id: 8,
    label: "Log Type",
    key: "logType",
    required: true,
    type: "select",
    options: LOG_TYPES,
    tooltip:
      "Select which type of logs to forward to Elasticsearch. This helps in organizing and querying logs within your indices.",
  },
];

export default function LogsOutputConfigForm() {
  const [form] = Form.useForm();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const outputType = queryParams.get("op") || "Splunk";
  const [loading, setLoading] = useState(false);
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [tooltipVisible, setTooltipVisible] = useState({});
  const [existingConfig, setExistingConfig] = useState(null);
  const [fetchingConfig, setFetchingConfig] = useState(true);

  // Get metadata for current output type
  const outputConfig = OUTPUT_CONFIG[outputType] || {
    color: "#666",
    title: `${outputType} Configuration`,
    subtitle: "Configure connection parameters",
    description: "Provide the connection details to integrate with your log destination.",
  };

  // Fetch existing configurations
  useEffect(() => {
    setFetchingConfig(true);

    axios
      .post(
        `${REACT_APP_API_TENANTS_URL}/getLogOutputConfiguration`,
        {
          orgId: ORG_ID(),
          tenantId: DOMAIN_ID(),
        },
        {
          headers: {
            withCredentials: true,
            "X-Authorization": AUTH_TOKEN(),
          },
        }
      )
      .then((response) => {
        setFetchingConfig(false);
        if (response.data && response.data.code !== 500) {
          setExistingConfig(response.data);

          // Populate form with existing values based on output type
          const configKey = getOutputTypeKey(outputType);
          if (response.data[configKey]) {
            const config = response.data[configKey];
            const formValues = {};

            // Map config values to form fields
            Object.keys(config).forEach((key) => {
              // Convert key to camelCase if needed
              const formKey = key.charAt(0).toLowerCase() + key.slice(1);
              if (config[key] !== 0 && config[key] !== "") {
                formValues[formKey] = config[key];
              }

              // Handle boolean values
              if (typeof config[key] === "boolean") {
                formValues[formKey] = config[key].toString();
              }
            });

            form.setFieldsValue(formValues);
          }
        }
      })
      .catch((error) => {
        setFetchingConfig(false);
        console.error("Error fetching log configurations:", error);
      });
  }, [outputType, form]);

  // Function to get correct case mapping for output type
  const getOutputTypeKey = (type) => {
    switch (type) {
      case "Splunk":
        return "splunk";
      case "HTTP":
        return "http";
      case "Syslog":
        return "syslog";
      case "Elasticsearch":
        return "elasticsearch";
      default:
        return type.toLowerCase();
    }
  };

  const toggleTooltip = (id) => {
    setTooltipVisible((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  // Function to get correct case for output type
  const getCorrectOutputName = (type) => {
    switch (type) {
      case "Splunk":
        return "Splunk";
      case "HTTP":
        return "Http";
      case "Syslog":
        return "Syslog";
      case "Elasticsearch":
        return "ElasticSearch";
      default:
        return type;
    }
  };

  const onFinish = (values) => {
    setLoading(true);

    // Transform form values to match API payload structure
    const payload = {
      orgId: ORG_ID(),
      tenantId: DOMAIN_ID(),
      outputName: getCorrectOutputName(outputType),
      host: values.host,
      uri: values.uri,
      httpMethod: "POST",
      useTls: values.useTls === "true" || values.tls === "true",
      tlsVerify: values.tlsVerify === "true",
      customHeaders: values.customHeaders ? JSON.parse(values.customHeaders) : null,
      jsonDateFormat: values.jsonDateFormat,
      jsonDateKey: values.jsonDateKey,
      indexName: values.indexName,
      syslogFormat: values.syslogFormat,
      messageKey: values.messageKey,
      messagePattern: values.messagePattern,
      mode: values.mode,
      password: values.password,
      authToken: values.authToken,
      httpUsername: values.httpUsername || values.httpUser,
      httpPassword: values.httpPassword || values.httpPasswd,
      port: parseInt(values.port, 10),

      workers: values.workers ? parseInt(values.workers, 10) : undefined,
      matchPattern: values.matchPattern,
      typesOfLogs: [values.logType],
    };

    // Determine if we're updating or creating a new config
    const endpoint = hasExistingConfig()
      ? `${REACT_APP_API_TENANTS_URL}/updateLogOutputConfiguration`
      : `${REACT_APP_API_TENANTS_URL}/storeLogOutputConfiguration`;

    axios
      .post(endpoint, payload, {
        headers: {
          withCredentials: true,
          "X-Authorization": AUTH_TOKEN(),
        },
      })
      .then((response) => {
        setLoading(false);
        if (response.data && response.data.status === "success") {
          message.success(
            `${outputType} output configuration ${
              hasExistingConfig() ? "updated" : "saved"
            } successfully!`
          );
          // Navigate back to the main logs config page after a short delay
          setTimeout(() => {
            window.location.href = "/pam/settings/logsConfig";
          }, 1500);
        } else if (response.data.code === 500) {
          message.error(response.data.message || "Not able to process log output configuration");
        } else {
          message.error(response.data.message || "Failed to save configuration");
        }
      })
      .catch((error) => {
        setLoading(false);
        console.error("Error saving log configuration:", error);

        if (error.response && error.response.status === 401) {
          message.error("Authentication error. Please log in again.");
        } else {
          message.error("Failed to save configuration. Please try again.");
        }
      });
  };

  const getParametersForOutputType = () => {
    switch (outputType) {
      case "Splunk":
        return SPLUNK_PARAMETERS;
      case "HTTP":
        return HTTP_PARAMETERS;
      case "Syslog":
        return SYSLOG_PARAMETERS;
      case "Elasticsearch":
        return ELASTICSEARCH_PARAMETERS;
      default:
        return SPLUNK_PARAMETERS;
    }
  };

  const getDescription = () => {
    switch (outputType) {
      case "Splunk":
        return "Configure your Splunk integration by providing the required connection details below. Mandatory fields include Host, Port, and Splunk Token. Optionally specify HTTP credentials for additional authentication.";
      case "HTTP":
        return "Configure your HTTP output by providing the endpoint details below. Mandatory fields include Host, Port, and URI. Optionally specify authentication credentials and output format.";
      case "Syslog":
        return "Configure your Syslog output by providing server details below. Mandatory fields include Host, Port, and Mode. Optionally specify Syslog Facility and Severity levels.";
      case "Elasticsearch":
        return "Configure your Elasticsearch integration by providing the required connection details below. Mandatory fields include Host, Port, and Index. Optionally specify HTTP credentials and Logstash format settings.";
      default:
        return "Configure your output integration by providing the required connection details below.";
    }
  };

  const parameters = getParametersForOutputType();

  // Custom styles to match the main page
  const cardStyle = {
    borderRadius: "1rem",
    overflow: "hidden",
    boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
    border: "none",
  };

  const buttonStyle = {
    borderRadius: "0.5rem",
    fontWeight: "500",
    height: "40px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  // Get the appropriate gradient color for the header based on output type
  const getHeaderColor = () => {
    switch (outputType) {
      case "Splunk":
        return "bg-gradient-to-r from-purple-500 to-indigo-600";
      case "HTTP":
        return "bg-gradient-to-r from-emerald-500 to-teal-600";
      case "Syslog":
        return "bg-gradient-to-r from-amber-500 to-orange-600";
      case "Elasticsearch":
        return "bg-gradient-to-r from-rose-500 to-pink-600";
      default:
        return "bg-gradient-to-r from-blue-500 to-indigo-600";
    }
  };

  // Check if configuration exists for the current output type
  const hasExistingConfig = () => {
    if (!existingConfig) return false;

    const config = existingConfig[getOutputTypeKey(outputType)];
    if (!config) return false;

    // Check if at least host and port are defined
    return config.Host && config.Port;
  };

  return (
    <div className="bg-gradient-to-br from-slate-50 to-slate-100 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8 pb-4 border-b border-slate-200">
          <div className="flex items-center space-x-4">
            <h1 className="text-2xl font-semibold text-slate-900">Logs Configuration</h1>
          </div>

          {/* Documentation link commented out */}
        </div>

        {fetchingConfig ? (
          <div className="flex justify-center items-center h-64">
            <Spin size="large" tip="Loading configuration..." />
          </div>
        ) : (
          <Row gutter={24}>
            {/* Main content */}
            <Col span={16}>
              <Card
                className="rounded-xl shadow-sm border border-slate-200 overflow-hidden"
                bodyStyle={{ padding: 0 }}
              >
                {/* Form header with icon */}
                <div className="border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white p-6">
                  <div className="flex items-center">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center mr-4"
                      style={{
                        backgroundColor: `${outputConfig.color}15`,
                      }}
                    >
                      <img
                        src={outputConfig.icon}
                        alt={outputType}
                        className="h-6 w-6 object-contain"
                      />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-slate-900">{outputConfig.title}</h2>
                      <p className="text-sm text-slate-500">
                        {hasExistingConfig()
                          ? `Edit your existing ${outputConfig.subtitle}`
                          : outputConfig.subtitle}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Form content */}
                <div className="p-6">
                  <Form form={form} layout="vertical" onFinish={onFinish} autoComplete="off">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {getParametersForOutputType().map((param) => (
                        <Form.Item
                          key={param.id}
                          label={
                            <div className="flex items-center space-x-1.5 mb-1.5">
                              <label className="text-sm font-medium text-slate-700">
                                {param.label}
                              </label>
                              {param.tooltip && (
                                <Tooltip
                                  title={param.tooltip}
                                  placement="right"
                                  overlayClassName="max-w-xs"
                                  color="#ffffff"
                                  overlayInnerStyle={{
                                    color: "#1f2937",
                                    padding: "8px 12px",
                                    fontSize: "0.875rem",
                                    boxShadow:
                                      "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                                    borderRadius: "6px",
                                  }}
                                >
                                  <InfoCircleOutlined className="text-slate-400 hover:text-slate-600 ml-1 cursor-help" />
                                </Tooltip>
                              )}
                            </div>
                          }
                          name={param.key}
                          rules={[
                            {
                              required: param.required,
                              message: `${param.label} is required`,
                            },
                          ]}
                        >
                          {param.type === "select" ? (
                            <Select placeholder={`Select ${param.label.toLowerCase()}`}>
                              {param.options.map((option) => (
                                <Option key={option} value={option}>
                                  {option}
                                </Option>
                              ))}
                            </Select>
                          ) : param.type === "textarea" ? (
                            <Input.TextArea
                              placeholder={
                                param.placeholder || `Enter ${param.label.toLowerCase()}`
                              }
                              className="rounded-md"
                              rows={4}
                            />
                          ) : (
                            <Input
                              type={
                                param.type === "password" || param.key.includes("password")
                                  ? "password"
                                  : "text"
                              }
                              placeholder={
                                param.placeholder || `Enter ${param.label.toLowerCase()}`
                              }
                              className="rounded-md"
                            />
                          )}
                        </Form.Item>
                      ))}
                    </div>

                    <div className="flex justify-end space-x-3 mt-8">
                      <Link to="/pam/settings/logsConfig">
                        <Button className="border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 hover:border-slate-400">
                          Cancel
                        </Button>
                      </Link>
                      <Button
                        type="primary"
                        htmlType="submit"
                        loading={loading}
                        style={{
                          borderColor: outputConfig.color,
                        }}
                        className="hover:opacity-90"
                      >
                        {loading
                          ? "Saving..."
                          : hasExistingConfig()
                          ? "Update Configuration"
                          : "Save Configuration"}
                      </Button>
                    </div>
                  </Form>
                </div>
              </Card>
            </Col>

            {/* Right sidebar */}
            <Col span={8}>
              <Card className="rounded-xl shadow-sm border border-slate-200 sticky top-8">
                <h3 className="text-lg font-semibold text-slate-800 mb-3">About {outputType}</h3>
                <p className="text-slate-600 text-sm leading-relaxed mb-5">
                  {outputConfig.description}
                </p>

                <div className="border-t border-slate-100 pt-5 mt-5">
                  <h4 className="text-sm font-medium text-slate-700 mb-3">
                    {hasExistingConfig() ? "Configuration Status" : "Setup Tips"}
                  </h4>

                  {hasExistingConfig() ? (
                    <div className="flex items-center space-x-2 bg-emerald-50 p-3 rounded-md">
                      <CheckCircleOutlined className="text-emerald-500" />
                      <span className="text-sm text-emerald-700">
                        Configuration exists for {outputType}
                      </span>
                    </div>
                  ) : (
                    <ul className="space-y-3 text-sm text-slate-600">
                      <li className="flex items-start">
                        <div className="h-5 w-5 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 mr-2 flex-shrink-0">
                          <span className="text-xs">1</span>
                        </div>
                        <span>
                          Ensure you have the correct host address and port for your {outputType}{" "}
                          instance.
                        </span>
                      </li>
                      <li className="flex items-start">
                        <div className="h-5 w-5 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 mr-2 flex-shrink-0">
                          <span className="text-xs">2</span>
                        </div>
                        <span>
                          Always use TLS when available for secure transmission of your logs.
                        </span>
                      </li>
                      <li className="flex items-start">
                        <div className="h-5 w-5 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 mr-2 flex-shrink-0">
                          <span className="text-xs">3</span>
                        </div>
                        <span>
                          After saving, check your {outputType} instance to confirm logs are being
                          received.
                        </span>
                      </li>
                    </ul>
                  )}
                </div>

                <div className="mt-6">{/* Documentation link commented out */}</div>
              </Card>
            </Col>
          </Row>
        )}
      </div>
    </div>
  );
}
